'use client';

import { useTranslations } from 'next-intl';
import type { FC } from 'react';
import { Dialog, DialogContent } from '@/app/components/ui/dialog';
import { toast } from '@/app/components/ui/use-toast';

const DEFAULT_NAME_MAX_LENGTH = 30;
const DEFAULT_DESCRIPTION_MAX_LENGTH = 200;

export interface VoiceInfoEditDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  name: string;
  description: string;
  onNameChange: (value: string) => void;
  onDescriptionChange: (value: string) => void;
  onCancel: () => void;
  onConfirm: () => void;
  isSaving?: boolean;
  nameMaxLength?: number;
  descriptionMaxLength?: number;
  /** Ensures label/input `id` pairs stay unique when multiple editors exist on one page */
  fieldIdSuffix?: string;
}

export const VoiceInfoEditDialog: FC<VoiceInfoEditDialogProps> = ({
  open,
  onOpenChange,
  name,
  description,
  onNameChange,
  onDescriptionChange,
  onCancel,
  onConfirm,
  isSaving = false,
  nameMaxLength = DEFAULT_NAME_MAX_LENGTH,
  descriptionMaxLength = DEFAULT_DESCRIPTION_MAX_LENGTH,
  fieldIdSuffix = '',
}) => {
  const t = useTranslations();
  const nameFieldId = fieldIdSuffix ? `voice-name-${fieldIdSuffix}` : 'voice-name';
  const descriptionFieldId = fieldIdSuffix ? `voice-description-${fieldIdSuffix}` : 'voice-description';
  const isNameEmpty = !name.trim();

  const handleConfirm = () => {
    if (isNameEmpty) {
      toast({ type: 'error', message: t('tools.voiceClone.validation.nameRequired') });
      return;
    }
    onConfirm();
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (isSaving) {
          return;
        }
        onOpenChange(nextOpen);
      }}
    >
      <DialogContent
        className="z-[60] w-[544px] max-w-[calc(100vw-32px)] gap-4 rounded-12 border-system-white04 bg-[#1d1d1d] p-8 text-system-white"
        overlayClassname="z-[60]"
        closeClassName="top-4 right-4 text-system-white/72 hover:text-system-white"
      >
        <div className="font-medium text-[20px] leading-[1.4]">{t('tools.voiceClone.voiceInfo.dialogTitle')}</div>
        <div className="space-y-4">
          <div className="space-y-2">
            <div className="flex items-start gap-2">
              <label htmlFor={nameFieldId} className="font-medium text-[16px] text-system-white leading-5">
                {t('tools.voiceClone.voiceInfo.nameLabel')}
              </label>
              <span className="text-sm text-system-white/40 leading-[1.6]">
                {name.length}/{nameMaxLength}
              </span>
            </div>
            <div className="rounded-12 border border-white/[0.06] bg-system-white04 px-4 py-3 focus-within:border-system-white24">
              <input
                id={nameFieldId}
                type="text"
                value={name}
                onChange={(e) => {
                  if (e.target.value.length <= nameMaxLength) {
                    onNameChange(e.target.value);
                  }
                }}
                placeholder={t('tools.voiceClone.voiceInfo.namePlaceholder')}
                className="h-[22px] w-full bg-transparent text-sm text-system-white leading-[1.6] outline-none placeholder:text-system-white/32"
              />
            </div>
          </div>
          <div className="space-y-2">
            <div className="flex items-start gap-2">
              <label htmlFor={descriptionFieldId} className="font-medium text-[16px] text-system-white leading-5">
                {t('tools.voiceClone.voiceInfo.descriptionLabel')}
              </label>
              <span className="text-sm text-system-white/40 leading-[1.6]">
                {description.length}/{descriptionMaxLength}
              </span>
            </div>
            <div className="rounded-8 border border-white/[0.06] bg-system-white04 px-4 py-3 focus-within:border-system-white24">
              <textarea
                id={descriptionFieldId}
                value={description}
                onChange={(e) => {
                  if (e.target.value.length <= descriptionMaxLength) {
                    onDescriptionChange(e.target.value);
                  }
                }}
                placeholder={t('tools.voiceClone.voiceInfo.descriptionPlaceholder')}
                rows={6}
                className="h-[176px] w-full resize-none bg-transparent text-sm text-system-white leading-[1.6] outline-none placeholder:text-system-white/32"
              />
            </div>
          </div>
        </div>
        <div className="flex gap-4">
          <button
            type="button"
            onClick={onCancel}
            disabled={isSaving}
            className="h-11 flex-1 rounded-12 border border-system-white10 px-5 text-sm text-system-white leading-7 transition hover:bg-system-white08 hover:text-system-white"
          >
            {t('tools.voiceClone.voiceInfo.cancel')}
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={isSaving}
            className="h-11 flex-1 rounded-12 bg-gradient-to-l from-[#94d8ff] from-[11.111%] to-[#e0ddff] px-5 font-bold text-black text-sm leading-7 transition hover:brightness-105"
          >
            {t('tools.voiceClone.voiceInfo.confirm')}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
